<template>
  <div class="invoice-management-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إصدار فاتورة مالية جديدة</h2>
        </div>
      </template>

      <!-- Step 1: Parent Selection -->
      <div class="parent-selection-section container">
        <el-alert
          title="الخطوة الأولى: تحديد ولي الأمر"
          type="success"
          description="ابدأ بالبحث عن ولي الأمر باستخدام الاسم أو رقم الهاتف لبدء عملية إنشاء الفاتورة."
          :closable="false"
          show-icon
        />
        <div class="search-bar">
          <el-select
            v-model="selectedParentId"
            filterable
            remote
            :remote-method="searchParents"
            :loading="parentsLoading"
            placeholder="ابحث عن ولي الأمر..."
            @change="handleParentSelection"
            style="width: 100%;"
            clearable
            size="large"
          >
            <el-option v-for="parent in parentsList" :key="parent.id" :label="`${parent.first_name} ${parent.last_name}`" :value="parent.id" />
          </el-select>
        </div>
      </div>
    </el-card>

    <!-- Invoice Creation Wizard Dialog -->
    <el-dialog v-model="dialogVisible" :title="`إنشاء فاتورة لـ: ${selectedParent?.first_name} ${selectedParent?.last_name}`" width="80%" @close="resetForm">
      <el-steps :active="activeStep" finish-status="success" simple style="margin-bottom: 20px;">
        <el-step title="تحديد الرسوم" :icon="Collection" />
        <el-step title="المراجعة والخصومات" :icon="Discount" />
        <el-step title="الدفع" :icon="Money" />
      </el-steps>

      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <!-- Step 1: Assign Fees -->
        <div v-if="activeStep === 0">
          <el-table :data="studentsOfParent" border style="width: 100%">
            <el-table-column label="الطالب" prop="name" />
            <el-table-column label="الفصل" prop="class.name" />
            <el-table-column label="الرسوم المطلوبة">
              <template #default="{ row }">
                <el-select
                  v-model="form.students[row.id].fee_structure_ids"
                  multiple
                  placeholder="اختر الرسوم"
                  style="width: 100%;"
                  @change="calculateTotalAmount"
                >
                  <el-option v-for="fee in feesForStudent(row)" :key="fee.id" :label="`${fee.fee_type.name} - ${fee.amount} دينار`" :value="fee.id" />
                </el-select>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Step 2: Review & Discounts -->
        <div v-if="activeStep === 1">
          <el-descriptions :column="1" border title="ملخص الفاتورة">
            <el-descriptions-item label="المبلغ الإجمالي">{{ totalAmount.toFixed(2) }} دينار</el-descriptions-item>
            <el-descriptions-item v-if="siblingDiscount > 0" label="خصم الإخوة">
              <el-checkbox v-model="form.apply_sibling_discount" @change="calculateTotalAmount" :disabled="form.manual_discount > 0">
                تطبيق خصم بنسبة ({{ siblingDiscountPercentage }}%) - قيمة الخصم: {{ calculatedDiscount.toFixed(2) }} دينار
              </el-checkbox>
            </el-descriptions-item>
            <el-descriptions-item label="قيمة الخصم اليدوي (دينار)">
              <el-input-number v-model="form.manual_discount" :min="0" :precision="2" :disabled="form.apply_sibling_discount" />
            </el-descriptions-item>
            <el-descriptions-item label="المبلغ النهائي بعد الخصم">
              <strong>{{ finalAmount.toFixed(2) }} دينار</strong>
            </el-descriptions-item>
          </el-descriptions>
        </div>
        
        <!-- Step 3: Payment -->
        <div v-if="activeStep === 2">
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item label="تاريخ إصدار الفاتورة" prop="issue_date">
                        <el-date-picker v-model="form.issue_date" type="date" style="width: 100%;" />
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="تاريخ الاستحقاق" prop="due_date">
                        <el-date-picker v-model="form.due_date" type="date" style="width: 100%;" />
                    </el-form-item>
                </el-col>
            </el-row>
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item label="الخزينة" prop="treasury_id">
                        <el-select v-model="form.treasury_id" placeholder="اختر الخزينة" style="width: 100%;">
                            <el-option v-for="treasury in treasuries" :key="treasury.id" :label="treasury.name" :value="treasury.id" />
                        </el-select>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="المبلغ المستلم كدفعة أولى" prop="amount_paid">
                        <el-input-number v-model="form.amount_paid" :min="0" :max="finalAmount" :precision="2" style="width: 100%;" />
                    </el-form-item>
                </el-col>
            </el-row>
        </div>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
            <el-button @click="prevStep" v-if="activeStep > 0">السابق</el-button>
            <el-button type="primary" @click="nextStep" v-if="activeStep < 2">التالي</el-button>
            <el-button type="success" @click="submitForm" :loading="isSaving" v-if="activeStep === 2">
                <el-icon><Finished /></el-icon> إنشاء الفاتورة
            </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Plus, Finished, Search, User, Collection, Discount, Money } from '@element-plus/icons-vue';
import debounce from 'lodash/debounce';

export default {
  name: 'InvoiceManagement',
  components: { Plus, Finished, Search, User, Collection, Discount, Money },
  data() {
    return {
      api: null,
      parentSearchQuery: '',
      parentsList: [],
      parentsLoading: false,
      studentsOfParent: [],
      feeStructures: [],
      treasuries: [],
      siblingDiscount: 0,
      siblingDiscountPercentage: 0,
      
      selectedParentId: null,
      selectedParent: null,
      isSaving: false,
      dialogVisible: false,
      activeStep: 0,

      form: this.getInitialForm(),
      rules: {
        issue_date: [{ required: true, message: 'تاريخ الإصدار مطلوب', trigger: 'change' }],
        due_date: [{ required: true, message: 'تاريخ الاستحقاق مطلوب', trigger: 'change' }],
        treasury_id: [{ required: true, message: 'الخزينة مطلوبة', trigger: 'change' }],
        amount_paid: [{ required: true, message: 'المبلغ المستلم مطلوب', trigger: 'blur' }],
      },
    };
  },
  computed: {
    totalAmount() {
      if (!this.selectedParent) return 0;
      let total = 0;
      for (const studentId in this.form.students) {
        if (this.form.students[studentId]?.fee_structure_ids) {
            this.form.students[studentId].fee_structure_ids.forEach(feeId => {
                const fee = this.feeStructures.find(f => f.id === feeId);
                if (fee) total += parseFloat(fee.amount);
            });
        }
      }
      return total;
    },
    calculatedDiscount() {
        return this.form.apply_sibling_discount ? (this.totalAmount * this.siblingDiscount) / 100 : 0;
    },
    finalAmount() {
        const discount = this.form.manual_discount > 0 ? this.form.manual_discount : this.calculatedDiscount;
        return this.totalAmount - discount;
    }
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.searchParents = debounce(this.searchParents, 500);
    this.fetchTreasuries();
  },
  methods: {
    getInitialForm() {
      return {
        parent_id: null,
        study_year_id: null,
        issue_date: new Date(),
        due_date: null,
        students: {},
        apply_sibling_discount: false,
        manual_discount: 0,
        treasury_id: null,
        amount_paid: 0,
      };
    },
    resetForm() {
        this.selectedParent = null;
        this.selectedParentId = null;
        this.studentsOfParent = [];
        this.form = this.getInitialForm();
        this.activeStep = 0;
    },
    async searchParents(query) {
      if (!query) {
        this.parentsList = [];
        return;
      }
      this.parentsLoading = true;
      try {
        const response = await this.api.get('/parents', { params: { search: query } });
        this.parentsList = response.data.data;
      } catch (error) {
        ElMessage.error('Failed to search for parents.');
      } finally {
        this.parentsLoading = false;
      }
    },
    async handleParentSelection(parentId) {
      if (!parentId) {
          this.resetForm();
          return;
      }
      this.form.parent_id = parentId;
      try {
        const response = await this.api.get(`/invoices/data-for-parent/${parentId}`);
        
        this.selectedParent = response.data.parent;
        this.studentsOfParent = response.data.students;
        this.feeStructures = response.data.feeStructures;
        
        const discountPolicy = response.data.siblingDiscountPolicy;
        if (discountPolicy) {
            this.siblingDiscount = parseFloat(discountPolicy.discount_percentage);
            this.siblingDiscountPercentage = discountPolicy.discount_percentage;
            this.form.apply_sibling_discount = true;
        } else {
            this.siblingDiscount = 0;
            this.siblingDiscountPercentage = 0;
            this.form.apply_sibling_discount = false;
        }

        this.studentsOfParent.forEach(student => {
            this.form.students[student.id] = { fee_structure_ids: [] };
        });

        if (this.studentsOfParent.length > 0) {
            this.form.study_year_id = this.studentsOfParent[0].study_year_id;
        }
        
        this.dialogVisible = true; // Open the wizard

      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'Failed to fetch parent details.');
        this.resetForm();
      }
    },
    feesForStudent(student) {
        if (!this.feeStructures || !student.study_year_id) {
            return [];
        }
        return this.feeStructures.filter(fee => fee.study_year_id === student.study_year_id);
    },
    async fetchTreasuries() {
      try {
        const response = await this.api.get('/treasuries');
        this.treasuries = response.data;
      } catch (error) {
        ElMessage.error('Failed to fetch treasuries.');
      }
    },
    calculateTotalAmount() {
        // Dummy method to trigger computed properties recalculation
    },
    nextStep() {
        if (this.activeStep < 2) this.activeStep++;
    },
    prevStep() {
        if (this.activeStep > 0) this.activeStep--;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.isSaving = true;
        
        const studentsWithFees = {};
        for (const studentId in this.form.students) {
            if (this.form.students[studentId].fee_structure_ids.length > 0) {
                studentsWithFees[studentId] = this.form.students[studentId];
            }
        }

        if (Object.keys(studentsWithFees).length === 0) {
            ElMessage.error('Please select at least one fee for one student to create an invoice.');
            this.isSaving = false;
            return;
        }

        const payload = {
            ...this.form,
            students: studentsWithFees,
        };

        try {
          const invoiceResponse = await this.api.post('/invoices', payload);
          ElMessage.success(invoiceResponse.data.message);
          this.dialogVisible = false;
          this.resetForm();
        } catch (error) {
          const errorMsg = error.response?.data?.message || 'Operation failed.';
          ElMessage.error(errorMsg);
        } finally {
          this.isSaving = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.invoice-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.parent-selection-section .search-bar { margin-top: 20px; }
.student-fee-card { margin-bottom: 20px; }
.student-fee-card h5 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #409EFF;
    display: flex;
    align-items: center;
    gap: 8px;
}
.summary-section { margin-top: 20px; margin-bottom: 20px; }
.dialog-footer { display: flex; justify-content: space-between; width: 100%; }
</style>
